import { useState, useEffect } from "react";
import { useWallet } from "@/context/WalletContext";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { 
  Trophy, 
  ListTodo, 
  History, 
  Sparkles,
  ShieldAlert
} from "lucide-react";

interface PointTransaction {
  id: string;
  amount: number;
  reason: string;
  details?: string;
  createdAt: number;
}

interface LeaderboardUser {
  handle: string;
  displayName: string;
  avatarUrl?: string;
  points: number;
}

export function PointsPanel() {
  const { t } = useTranslation();
  const { xProfile } = useWallet() as any;

  const [activeTab, setActiveTab] = useState<"leaderboard" | "history">("leaderboard");
  const [pointsData, setPointsData] = useState<{ points: number; rank: number; transactions: PointTransaction[] } | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!xProfile) return;
    
    setLoading(true);
    // Fetch user points and transaction history
    fetch("/api/points/me")
      .then(res => res.json())
      .then(data => {
        if (data && !data.error) setPointsData(data);
      })
      .catch(err => console.error("Error fetching points:", err))
      .finally(() => setLoading(false));

    // Fetch leaderboard
    fetch("/api/points/leaderboard")
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setLeaderboard(data);
      })
      .catch(err => console.error("Error fetching leaderboard:", err));
  }, [xProfile]);

  const mapReasonText = (reason: string) => {
    switch (reason) {
      case "wallet_synced": return "Synced Wallet offline";
      case "limit_order_created": return "Placed a limit order";
      case "limit_order_executed": return "Limit order filled successfully";
      case "copy_trade_setup": return "Set up copy-trade subscription";
      case "copy_trade_filled": return "Offline copy trade filled";
      case "alert_created": return "Set a wallet alert";
      case "alert_triggered": return "Wallet alert triggered";
      default: return reason.replace(/_/g, " ");
    }
  };

  const getRankBadge = (points: number) => {
    if (points >= 5000) return { title: "Gold Whale", color: "from-yellow-400 to-amber-500 text-amber-950" };
    if (points >= 1500) return { title: "Silver Scalper", color: "from-slate-200 to-slate-400 text-slate-900" };
    return { title: "Bronze Trader", color: "from-amber-600 to-amber-800 text-amber-100" };
  };

  if (!xProfile) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <ShieldAlert className="size-12 mb-3 text-violet-400 animate-pulse" />
        <h3 className="text-[16px] font-semibold text-white mb-1">
          {t("wallet.xAuthTitle")}
        </h3>
        <p className="text-[12px] text-violet-200/50 max-w-sm">
          {t("wallet.xPrompt")}
        </p>
      </div>
    );
  }

  const userPoints = pointsData?.points ?? 0;
  const userRank = pointsData?.rank ?? "—";
  const userTransactions = pointsData?.transactions ?? [];
  const badge = getRankBadge(userPoints);

  // Check which tasks the user has completed based on points history
  const hasTask = (reason: string) => userTransactions.some(tx => tx.reason === reason);

  const tasks = [
    { label: "Sync a Wallet to Server", pts: 50, completed: hasTask("wallet_synced") },
    { label: "Create a Limit Order", pts: 50, completed: hasTask("limit_order_created") },
    { label: "Configure a Wallet Alert", pts: 25, completed: hasTask("alert_created") },
    { label: "Add a Whale to Copy Trade", pts: 100, completed: hasTask("copy_trade_setup") },
    { label: "Perform a Copy Trade", pts: 100, completed: hasTask("copy_trade_filled") },
  ];

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#07070e] font-sans text-white p-4 space-y-4">
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Trophy className="size-5 text-yellow-400 animate-bounce" />
          <h2 className="text-[16px] font-bold bg-gradient-to-r from-teal-200 to-violet-200 bg-clip-text text-transparent">
            {t("points.title")}
          </h2>
        </div>
        <span className="shrink-0 flex items-center gap-1 text-[10px] text-violet-300 bg-violet-500/10 border border-violet-500/20 px-2 py-0.5 rounded-full font-mono uppercase tracking-wide">
          <Sparkles className="size-3 text-cyan-300" />
          Multiplier 1.0x
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 shrink-0">
        {/* User Card */}
        <div className="relative rounded-2xl border border-yellow-400/20 bg-gradient-to-r from-purple-900/40 to-indigo-900/40 p-4 shadow-xl overflow-hidden group">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:scale-110 transition-transform duration-300">
            <Trophy className="size-24 text-yellow-400" />
          </div>
          <div className="relative flex flex-col justify-between h-full">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-violet-200/50">
                Your Total Points
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-[28px] font-mono font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-400">
                  {userPoints.toLocaleString()}
                </span>
                <span className="text-[10px] font-semibold text-yellow-300/80">PTS</span>
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
              <div>
                <span className="text-[9px] font-bold uppercase tracking-wider text-violet-200/40 block">Global Rank</span>
                <span className="text-[14px] font-mono font-bold text-cyan-300">#{userRank}</span>
              </div>
              <div className="text-right">
                <span className="text-[9px] font-bold uppercase tracking-wider text-violet-200/40 block">League Level</span>
                <span className={`inline-block text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-gradient-to-r ${badge.color}`}>
                  {badge.title}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Gamified Checklist */}
        <div className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-[#0c0c18] p-4 flex flex-col justify-between shadow-inner">
          <div className="flex items-center gap-1.5 mb-2">
            <ListTodo className="size-4 text-cyan-300" />
            <h3 className="text-[12px] font-bold text-violet-200/70 uppercase tracking-wider">
              {t("points.tasksTitle")}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {tasks.map((task, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-2 rounded-lg border text-[11px] ${task.completed ? "bg-emerald-500/5 border-emerald-500/10 text-emerald-300/80" : "bg-black/30 border-white/[0.04] text-white/50"}`}
              >
                <span className="truncate pr-1">{task.label}</span>
                <span className={`shrink-0 font-mono text-[9px] font-bold px-1.5 py-0.5 rounded ${task.completed ? "bg-emerald-500/20 text-emerald-300" : "bg-cyan-500/10 text-cyan-300"}`}>
                  {task.completed ? "✓" : `+${task.pts}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Leaderboard and Points History tabs */}
      <div className="flex-1 flex flex-col min-h-0 bg-[#0c0c18] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="flex border-b border-white/[0.06] bg-black/45 px-3 py-1.5 justify-between items-center shrink-0">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("leaderboard")}
              className={`flex items-center gap-1 text-[12px] font-bold px-3 py-1.5 rounded-lg transition-all ${activeTab === "leaderboard" ? "bg-purple-600/15 text-purple-300 border border-purple-500/20" : "text-white/40 hover:text-white/80"}`}
            >
              <Trophy className="size-3.5" />
              {t("points.leaderboardTitle")}
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`flex items-center gap-1 text-[12px] font-bold px-3 py-1.5 rounded-lg transition-all ${activeTab === "history" ? "bg-purple-600/15 text-purple-300 border border-purple-500/20" : "text-white/40 hover:text-white/80"}`}
            >
              <History className="size-3.5" />
              My Points Ledger
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3">
          {activeTab === "leaderboard" ? (
            <div className="w-full">
              {leaderboard.length === 0 ? (
                <div className="text-[12px] text-white/30 italic py-4 text-center">Loading leaderboard data...</div>
              ) : (
                <div className="divide-y divide-white/[0.03]">
                  {leaderboard.map((user, i) => {
                    const isCurrentUser = user.handle === xProfile.username;
                    const place = i + 1;
                    return (
                      <div 
                        key={i} 
                        className={`flex items-center justify-between py-2 px-3 rounded-lg ${isCurrentUser ? "bg-purple-500/10 border border-purple-500/20" : ""}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-6 text-center font-mono font-extrabold text-[12px] ${place === 1 ? "text-yellow-400" : place === 2 ? "text-slate-300" : place === 3 ? "text-amber-600" : "text-white/30"}`}>
                            {place === 1 ? "🥇" : place === 2 ? "🥈" : place === 3 ? "🥉" : `#${place}`}
                          </span>
                          {user.avatarUrl ? (
                            <img src={user.avatarUrl} alt={user.handle} className="size-6 rounded-full border border-white/10" />
                          ) : (
                            <div className="size-6 rounded-full bg-violet-600/30 flex items-center justify-center font-bold text-[10px] text-violet-300 border border-violet-500/20">
                              {user.displayName.slice(0, 1)}
                            </div>
                          )}
                          <div>
                            <span className="text-[12px] font-bold text-white/90">{user.displayName}</span>
                            <span className="text-[9px] text-white/30 block">@{user.handle}</span>
                          </div>
                        </div>
                        <span className="text-[13px] font-mono font-bold text-yellow-300">
                          {user.points.toLocaleString()} <span className="text-[8px] text-white/30">PTS</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="w-full">
              {loading ? (
                <div className="text-[12px] text-white/30 italic py-4 text-center">{t("panel.loading")}</div>
              ) : userTransactions.length === 0 ? (
                <div className="text-[11px] text-white/20 italic py-8 text-center">
                  No points activity logged. Perform some actions (sync keys, add alerts, trade) to load points ledger!
                </div>
              ) : (
                <div className="space-y-2">
                  {userTransactions.map((tx) => (
                    <div 
                      key={tx.id} 
                      className="flex items-center justify-between p-2 rounded-lg border border-white/[0.04] bg-white/[0.01]"
                    >
                      <div>
                        <div className="text-[11px] font-semibold text-white/80">
                          {mapReasonText(tx.reason)}
                        </div>
                        <div className="text-[9px] text-white/30 mt-0.5 font-mono truncate max-w-sm lg:max-w-xl">
                          {tx.details ? tx.details : `Tx ID: ${tx.id}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[12px] font-mono font-extrabold text-emerald-400">
                          +{tx.amount} <span className="text-[8px]">PTS</span>
                        </div>
                        <div className="text-[8px] text-white/20 font-mono mt-0.5">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
